import { ReactNode } from 'react'
import { match, P } from '@st/pattern'
import { BoxConstraints, RenderBox } from '../rendering'
import { renderElement } from '../document'

export type FlexProps = {
  direction?: 'row' | 'column'
  gap?: number
  mainAxisAlignment?: 'start' | 'center' | 'end' | 'spaceBetween' | 'spaceAround' | 'spaceEvenly'
  crossAxisAlignment?: 'start' | 'center' | 'end' | 'stretch'
  mainAxisSize?: 'max' | 'min'
}

/**
 * Flutter-style two-pass flex layout for the supported alignment modes.
 * Reference: packages/flutter/lib/src/rendering/flex.dart in Flutter.
 *
 * Inflexible children are laid out first with an unbounded main axis, so they
 * take their intrinsic extent; a child that must fill space says so with
 * Expanded. The remaining main-axis space is then divided among children with
 * a positive flex factor. Expanded requires the whole share; Flexible allows
 * a smaller child with a loose minimum. The
 * cross axis is loose up to the incoming maximum, or tight to it for 'stretch'.
 *
 * The flex sizes itself to constraints.constrain(ideal × cross), where the
 * ideal main extent is the incoming maximum for mainAxisSize 'max' and the
 * children's total extent otherwise. Content wider than that overflows and is
 * painted unclipped, as in Flutter.
 */
export class FlexRenderBox extends RenderBox {
  performLayout(c: BoxConstraints) {
    const {
      direction = 'column',
      gap = 0,
      mainAxisAlignment = 'start',
      crossAxisAlignment = 'start',
      mainAxisSize = 'max'
    } = this.props as FlexProps
    const isRow = direction === 'row'
    const axis = flexAxis(direction)
    const [minMain, maxMain] = axis.main(c)
    const [minCross, maxCross] = axis.cross(c)
    const stretch = crossAxisAlignment === 'stretch'
    const canFlex = Number.isFinite(maxMain)
    let minChildCross = 0
    if (stretch) minChildCross = maxCross
    const childConstraints = (minMain: number, maxMain: number) =>
      axis.constraints([minMain, maxMain], [minChildCross, maxCross])

    // ------------------------------------------------------------------
    // FIRST PASS → size inflexible children & collect flex information
    // ------------------------------------------------------------------
    const childCount = this.children.length
    let allocated = gap * Math.max(childCount - 1, 0)
    let flexSum = 0

    this.children.forEach((ch) => {
      if (flexOf(ch) > 0) {
        if (canFlex) {
          flexSum += flexOf(ch)
          return
        }
        if (mainAxisSize == 'max' || ch.props.fit != 'loose') {
          throw new Error(`Flex child requires bounded main-axis constraints in ${direction}`)
        }
      }
      ch.performLayout(childConstraints(0, Infinity))
      allocated += axis.extent(ch).main
    })

    // ------------------------------------------------------------------
    // SECOND PASS → layout flex (Expanded) children
    // ------------------------------------------------------------------
    const free = Math.max(maxMain - allocated, 0)
    this.children.forEach((ch) => {
      if (!canFlex || flexOf(ch) == 0) return
      const alloc = (free * flexOf(ch)) / flexSum
      const min = match(ch.props.fit)
        .with('loose', () => 0)
        .otherwise(() => alloc)
      ch.performLayout(childConstraints(min, alloc))
      allocated += axis.extent(ch).main
    })

    let cross = Math.max(0, ...this.children.map((ch) => axis.extent(ch).cross))
    if (stretch) cross = maxCross
    cross = Math.min(Math.max(cross, minCross), maxCross)

    // ---------------------------------------------------------------------
    // Alignment & positioning pass
    // ---------------------------------------------------------------------
    let idealMain = allocated
    if (mainAxisSize === 'max' && canFlex) idealMain = maxMain
    const containerMain = Math.min(Math.max(idealMain, minMain), maxMain)
    const main = allocated

    const remaining = Math.max(containerMain - main, 0)

    // As in Flutter, alignment distributes only the space left after children
    // and gaps; the gap is then added between children, never at the edges.
    const [leading, distributed] = match(mainAxisAlignment)
      .with('start', () => [0, 0])
      .with('center', () => [remaining / 2, 0])
      .with('end', () => [remaining, 0])
      .with('spaceBetween', () => {
        if (childCount < 2) return [0, 0]
        return [0, remaining / (childCount - 1)]
      })
      .with('spaceAround', () => {
        if (childCount == 0) return [0, 0]
        return [remaining / childCount / 2, remaining / childCount]
      })
      .with('spaceEvenly', () => [remaining / (childCount + 1), remaining / (childCount + 1)])
      .exhaustive()
    const between = distributed + gap

    let cursor = leading
    this.children.forEach((ch) => {
      const extent = axis.extent(ch)
      const crossOffset = match(crossAxisAlignment)
        .with('center', () => (cross - extent.cross) / 2)
        .with('end', () => cross - extent.cross)
        .with(P.union('start', 'stretch'), () => 0)
        .exhaustive()
      // Stretch was applied during layout through tight cross-axis constraints.
      // Positioning must not resize children after they have laid out descendants.
      if (isRow) {
        ch.box.x = cursor
        ch.box.y = crossOffset
      } else {
        ch.box.x = crossOffset
        ch.box.y = cursor
      }
      cursor += extent.main + between
    })

    // own size is the space actually used
    this.box.width = isRow ? containerMain : cross
    this.box.height = isRow ? cross : containerMain
  }
}

// Flutter's flex factor: only a positive factor makes a child flexible, so an
// Expanded with flex 0 is laid out like any inflexible child.
function flexOf(child: RenderBox) {
  if (child instanceof ExpandedRenderBox) return child.props.flex ?? 1
  return 0
}

type Range = [min: number, max: number]

function flexAxis(direction: 'row' | 'column') {
  return match(direction)
    .with('row', () => ({
      main: (c: BoxConstraints): Range => [c.minWidth, c.maxWidth],
      cross: (c: BoxConstraints): Range => [c.minHeight, c.maxHeight],
      constraints: ([minWidth, maxWidth]: Range, [minHeight, maxHeight]: Range) => ({
        minWidth,
        maxWidth,
        minHeight,
        maxHeight
      }),
      extent: ({ box }: RenderBox) => ({ main: box.width, cross: box.height })
    }))
    .with('column', () => ({
      main: (c: BoxConstraints): Range => [c.minHeight, c.maxHeight],
      cross: (c: BoxConstraints): Range => [c.minWidth, c.maxWidth],
      constraints: ([minHeight, maxHeight]: Range, [minWidth, maxWidth]: Range) => ({
        minWidth,
        maxWidth,
        minHeight,
        maxHeight
      }),
      extent: ({ box }: RenderBox) => ({ main: box.height, cross: box.width })
    }))
    .exhaustive()
}

export class ExpandedRenderBox extends RenderBox<{ flex?: number; fit?: 'tight' | 'loose' }> {
  performLayout(c: BoxConstraints) {
    const child = this.children[0]

    // Spacer occupies the tight main-axis slice and the minimum cross extent
    // (zero for loose constraints, the incoming maximum for stretch).
    if (!child) {
      this.box = { x: 0, y: 0, width: c.minWidth, height: c.minHeight }
      return
    }

    // The flex parent supplies main-axis constraints according to fit; the cross axis is
    // loose unless the parent selected stretch. The child must honor both.
    child.performLayout(c)

    // Copy the child's size; do not stretch on the cross‑axis
    this.box = {
      x: 0,
      y: 0,
      width: child.box.width,
      height: child.box.height
    }

    child.box.x = 0
    child.box.y = 0
  }
}

export function Flex({
  children,
  direction = 'column',
  gap = 0,
  mainAxisAlignment = 'start',
  crossAxisAlignment = 'start',
  mainAxisSize = 'max'
}: FlexProps & { children: ReactNode }) {
  return renderElement(
    new FlexRenderBox({
      direction,
      gap,
      mainAxisAlignment,
      crossAxisAlignment,
      mainAxisSize
    }),
    children
  )
}

export function Row({
  children,
  gap = 0,
  mainAxisAlignment = 'start',
  crossAxisAlignment = 'start',
  mainAxisSize = 'max'
}: FlexProps & { children: ReactNode }) {
  return (
    <Flex
      direction="row"
      gap={gap}
      mainAxisAlignment={mainAxisAlignment}
      crossAxisAlignment={crossAxisAlignment}
      mainAxisSize={mainAxisSize}
    >
      {children}
    </Flex>
  )
}

export function Column({
  children,
  gap = 0,
  mainAxisAlignment = 'start',
  crossAxisAlignment = 'start',
  mainAxisSize = 'max'
}: FlexProps & { children: ReactNode }) {
  return (
    <Flex
      direction="column"
      gap={gap}
      mainAxisAlignment={mainAxisAlignment}
      crossAxisAlignment={crossAxisAlignment}
      mainAxisSize={mainAxisSize}
    >
      {children}
    </Flex>
  )
}

export function Expanded({ children, flex = 1 }: { children: ReactNode; flex?: number }) {
  return renderElement(new ExpandedRenderBox({ flex }), children)
}

/** Flutter's loose Flexible: a child may use less than its allocated share. */
export function Flexible({ children, flex = 1 }: { children: ReactNode; flex?: number }) {
  return renderElement(new ExpandedRenderBox({ flex, fit: 'loose' }), children)
}

/** Flutter's Spacer: empty space that takes a flex share of the main axis. */
export function Spacer({ flex = 1 }: { flex?: number }) {
  return renderElement(new ExpandedRenderBox({ flex }))
}
